import { type CustomField } from '~/models/customData/types';
import { DeliveryNoteObject } from '~/models/deliveries/DeliveryNote';
import { FilterGroupFilter } from '~/models/filters/FilterGroupFilter';
import { type FeatureFlags } from '~/models/masterdata/UserFeatureFlags';

type P = {
  customFields: CustomField[];
  featureFlags: FeatureFlags;
  isPermittedSiteAllowedUser: boolean;
  isSelectableFilterDisabled: (key: string) => boolean;
};

export const getSelectableFilters = ({
  customFields = [],
  featureFlags = {} as FeatureFlags,
  isPermittedSiteAllowedUser,
  isSelectableFilterDisabled,
}: P) => {
  const canFilterBillingStatus = featureFlags?.disableInvoices === false;

  const filtersStandard = [
    {
      disabled: isSelectableFilterDisabled(
        DeliveryNoteObject.PROPERTY.PROCESS_STATE.KEY,
      ),
      id: FilterGroupFilter.FILTER.SELECTED_PROCESS_STATE,
      name: DeliveryNoteObject.PROPERTY.PROCESS_STATE.STRING,
    },
    {
      disabled: isSelectableFilterDisabled(
        DeliveryNoteObject.PROPERTY.ACCEPT_STATE.KEY,
      ),
      id: FilterGroupFilter.FILTER.SELECTED_ACCEPT_STATE,
      name: DeliveryNoteObject.PROPERTY.ACCEPT_STATE.STRING,
    },
    canFilterBillingStatus
      ? {
          disabled: isSelectableFilterDisabled(
            DeliveryNoteObject.PROPERTY.SETTLED_STATUS.KEY,
          ),
          id: FilterGroupFilter.FILTER.SELECTED_SETTLED_STATUS,
          name: DeliveryNoteObject.PROPERTY.SETTLED_STATUS.STRING,
        }
      : undefined,
    {
      disabled: isSelectableFilterDisabled(
        DeliveryNoteObject.PROPERTY.TO_SITE_RECIPIENT.KEY,
      ),
      id: FilterGroupFilter.FILTER.SELECTED_TO_SITE_RECIPIENT,
      name: DeliveryNoteObject.PROPERTY.TO_SITE_RECIPIENT.STRING,
    },
    {
      disabled: isSelectableFilterDisabled(
        DeliveryNoteObject.PROPERTY.TO_SITE_SUPPLIER.KEY,
      ),
      id: FilterGroupFilter.FILTER.SELECTED_TO_SITE_SUPPLIER,
      name: DeliveryNoteObject.PROPERTY.TO_SITE_SUPPLIER.STRING,
    },
    {
      disabled: isSelectableFilterDisabled(
        DeliveryNoteObject.PROPERTY.CATEGORY.KEY,
      ),
      id: FilterGroupFilter.FILTER.SELECTED_CATEGORY,
      name: DeliveryNoteObject.PROPERTY.CATEGORY.STRING,
    },
    {
      disabled: isSelectableFilterDisabled(
        DeliveryNoteObject.PROPERTY.COST_CENTER.KEY,
      ),
      id: FilterGroupFilter.FILTER.SELECTED_COST_CENTER,
      name: DeliveryNoteObject.PROPERTY.COST_CENTER.STRING,
    },
    {
      disabled: isSelectableFilterDisabled(
        DeliveryNoteObject.PROPERTY.ARTICLE_NUMBER.KEY,
      ),
      id: FilterGroupFilter.FILTER.SELECTED_ARTICLE_NUMBER,
      name: DeliveryNoteObject.PROPERTY.ARTICLE_NUMBER.STRING,
    },
    {
      disabled: isSelectableFilterDisabled(
        DeliveryNoteObject.PROPERTY.ARTICLE.KEY,
      ),
      id: FilterGroupFilter.FILTER.SELECTED_ARTICLE,
      name: DeliveryNoteObject.PROPERTY.ARTICLE.STRING,
    },
    {
      disabled: isSelectableFilterDisabled(
        DeliveryNoteObject.PROPERTY.SUPPLIER.KEY,
      ),
      id: FilterGroupFilter.FILTER.SELECTED_SUPPLIER,
      name: DeliveryNoteObject.PROPERTY.SUPPLIER.STRING,
    },
    {
      disabled: isSelectableFilterDisabled(
        DeliveryNoteObject.PROPERTY.RECIPIENT.KEY,
      ),
      id: FilterGroupFilter.FILTER.SELECTED_RECIPIENT,
      name: DeliveryNoteObject.PROPERTY.RECIPIENT.STRING,
    },
    {
      disabled: isSelectableFilterDisabled(
        DeliveryNoteObject.PROPERTY.FROM_SITE.KEY,
      ),
      id: FilterGroupFilter.FILTER.SELECTED_FROM_SITE,
      name: DeliveryNoteObject.PROPERTY.FROM_SITE.STRING,
    },
    {
      disabled: false,
      id: FilterGroupFilter.FILTER.SELECTED_OU_ID,
      name: DeliveryNoteObject.PROPERTY.ORGANIZATIONAL_UNIT.STRING,
    },
    {
      disabled: false,
      id: FilterGroupFilter.FILTER.SELECTED_CUSTOMER_NUMBER,
      name: DeliveryNoteObject.PROPERTY.CUSTOMER_NUMBER.STRING,
    },
    {
      disabled: false,
      id: FilterGroupFilter.FILTER.SELECTED_SOURCE,
      name: DeliveryNoteObject.PROPERTY.SOURCE.STRING,
    },
    {
      disabled: false,
      id: FilterGroupFilter.FILTER.SELECTED_SUBMITTED_BY_USER_ID,
      name: DeliveryNoteObject.PROPERTY.SUBMITTED_BY_USER.STRING,
    },
  ];

  const canFilterPermittedSites =
    featureFlags.accessPermittedSites || isPermittedSiteAllowedUser;

  const filtersPermittedSites = canFilterPermittedSites
    ? [
        {
          disabled: isSelectableFilterDisabled(
            DeliveryNoteObject.PROPERTY.PERMITTED_TO_SITE_NAMES.KEY,
          ),
          id: FilterGroupFilter.FILTER.SELECTED_PERMITTED_TO_SITES,
          name: DeliveryNoteObject.PROPERTY.PERMITTED_TO_SITE_NAMES.STRING,
        },
        {
          disabled: isSelectableFilterDisabled(
            DeliveryNoteObject.PROPERTY.PERMITTED_COST_CENTER_NAMES.KEY,
          ),
          id: FilterGroupFilter.FILTER.SELECTED_PERMITTED_COST_CENTERS,
          name: DeliveryNoteObject.PROPERTY.PERMITTED_COST_CENTER_NAMES.STRING,
        },
      ]
    : [];

  const filtersCustomFields = customFields?.map((customField) => ({
    customField,
    disabled: isSelectableFilterDisabled(customField.key),
    id: customField.key,
    name: customField.displayName,
    type: customField.type,
  }));

  const selectableFilters = [
    ...filtersStandard,
    ...filtersPermittedSites,
    ...filtersCustomFields,
  ].filter(Boolean);

  return selectableFilters;
};
